// One item's spec: the draft text (English, 中文, internal notes) in the formatted editor, its files, its released
// revisions and Release. The item panel shows it narrow; "Open" and the Specs page show it wide, in the side pane.
// Edits stay local until saved (Ctrl/⌘+S, leaving the text box, switching tabs or closing); a draft someone else
// saved meanwhile is never overwritten without asking.
import { FileText, Maximize2, Rocket } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { Item, Spec } from "../../../../shared/db.gen.ts";
import { typeLabel } from "../../../../shared/part-type.ts";
import { SPEC_STATUS_TONE, type SpecStatus, specStatusLabel } from "../../../../shared/specs.ts";
import { can, mutate } from "../../lib/mutate.ts";
import { useRow } from "../../lib/sync.ts";
import { Badge, Button, cx } from "../../ui/controls.tsx";
import { Tabs } from "../../ui/PageParts.tsx";
import { PaneHead, PaneLink, usePane, usePaneLevel } from "../../ui/SidePane.tsx";
import { BoardFacts } from "./BoardFacts.tsx"; // KiCad projects ---
import { ItemBomStrip } from "./ItemBomStrip.tsx";
import { type SpecIndex, startSpec, statusOf, useSpecIndex } from "./lib.ts";
import { MarkdownEditor, type TextUpdate } from "./MarkdownEditor.tsx";
import { SpecFiles } from "./SpecFiles.tsx";
import { ReleaseDialog, SpecRevisions } from "./SpecRevisions.tsx";

type TextField = "draft_md" | "draft_md_zh" | "internal_md";
const FIELDS: TextField[] = ["draft_md", "draft_md_zh", "internal_md"];
type Tab = "spec" | "zh" | "internal" | "files" | "revisions";
const TAB_FIELD: Partial<Record<Tab, TextField>> = { spec: "draft_md", zh: "draft_md_zh", internal: "internal_md" };

export function SpecStatusBadge({ status }: { status: SpecStatus }) {
  return <Badge tone={SPEC_STATUS_TONE[status.kind]}>{specStatusLabel(status)}</Badge>;
}

// Manufacturing projects --- materials, sub-assemblies and services are in the Parts library
export const itemPath = (item: Pick<Item, "id" | "kind">): string | null => `${item.kind === "sell" ? "/items/sell" : "/parts"}?open=${item.id}`;

export function NoSpec({ item }: { item: Item }) {
  return (
    <div className="flex items-center gap-2 px-2 py-1">
      <span className="text-muted">No spec yet.</span>
      {can("spec.create") && (
        <Button size="sm" variant="ghost" icon={<FileText className="size-3.5" />} onClick={() => startSpec(item.id)}>
          Start its spec
        </Button>
      )}
    </div>
  );
}

/**
 * `inBom`: shown in its BOM's Spec tab, so without the strip pointing at the BOM; `releasedAbove`: that BOM is a draft,
 * whose Release (above) releases this spec with it, so the spec has no Release of its own there.
 */
export function SpecWorkspace({
  item,
  spec,
  ix,
  wide,
  inBom,
  releasedAbove,
  onExpand,
}: {
  item: Item;
  spec: Spec;
  ix: SpecIndex;
  wide?: boolean;
  inBom?: boolean;
  releasedAbove?: boolean;
  onExpand?: () => void;
}) {
  const [tab, setTab] = useState<Tab>("spec");
  const [local, setLocal] = useState<Partial<Record<TextField, string>>>({});
  const [releasing, setReleasing] = useState(false);
  // What each field said when local editing began, to notice someone else saving meanwhile.
  const base = useRef<Partial<Record<TextField, string>>>({});
  const localRef = useRef(local);
  localRef.current = local;
  const specRef = useRef(spec);
  specRef.current = spec;
  const editable = can("spec.update");
  const status = statusOf(ix, spec);
  const dirty = FIELDS.some((f) => local[f] !== undefined && local[f] !== spec[f]);

  const change = (f: TextField) => (next: TextUpdate) =>
    setLocal((prev) => {
      const current = prev[f] ?? specRef.current[f];
      if (prev[f] === undefined) base.current[f] = specRef.current[f];
      return { ...prev, [f]: typeof next === "function" ? next(current) : next };
    });

  const save = useCallback(async (): Promise<boolean> => {
    const s = specRef.current;
    const patch: Partial<Record<TextField, string>> = {};
    for (const f of FIELDS) {
      const v = localRef.current[f];
      if (v !== undefined && v !== s[f]) patch[f] = v;
    }
    const keys = Object.keys(patch) as TextField[];
    if (!keys.length) return true;
    if (keys.some((f) => base.current[f] !== undefined && base.current[f] !== s[f])) {
      if (!confirm("Someone else saved this draft while you were editing it. Save your version over theirs?")) return false;
    }
    const ok = await mutate("spec.update", { id: s.id, patch });
    if (ok) {
      setLocal((prev) => {
        const next = { ...prev };
        for (const f of keys) {
          // Typed on while it saved: keep the newer text, now based on what was saved.
          if (next[f] === patch[f]) {
            delete next[f];
            delete base.current[f];
          } else base.current[f] = patch[f];
        }
        return next;
      });
    }
    return ok;
  }, []);

  // Closing the panel or the editor saves what's left; leaving the page with unsaved text asks first.
  useEffect(() => () => void save(), [save]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const files = ix.filesBySpec.get(spec.id)?.length ?? 0;
  const revs = ix.revisionsBySpec.get(spec.id)?.length ?? 0;
  const tabs: [Tab, string][] = [
    ["spec", "Spec"],
    ["zh", "中文"],
    ["internal", "Internal"],
    ["files", files ? `Files ${files}` : "Files"],
    ["revisions", revs ? `Revisions ${revs}` : "Revisions"],
  ];
  const field = TAB_FIELD[tab];
  // A product's spec (migration 059) covers every variant: named by the product.
  const title = spec.shopify_product_id
    ? item.shopify_product_title || item.name
    : item.shopify_variant_title
      ? `${item.shopify_product_title} · ${item.shopify_variant_title}`
      : item.name;

  return (
    <div
      className={cx("flex min-h-0 flex-col", wide && "h-full")}
      onKeyDown={(e) => {
        const el = e.target as HTMLElement;
        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
          e.preventDefault();
          save();
        } else if (e.key === "Escape" && !e.defaultPrevented && e.currentTarget.contains(el) && (el.isContentEditable || el.tagName === "TEXTAREA")) {
          // Esc leaves the text, which saves it; the next Esc goes back in the side pane
          e.preventDefault();
          el.blur();
        }
      }}
    >
      <div className="flex items-center gap-2 px-2 pb-1.5">
        <SpecStatusBadge status={status} />
        {dirty && (
          <button type="button" onClick={() => save()} className="text-[12px] text-warn hover:underline" title="Save (Ctrl+S)">
            Unsaved · save
          </button>
        )}
        <span className="ml-auto flex items-center gap-1">
          {releasedAbove ? (
            <span className="text-[12px] text-muted">Released with the BOM: Release, above</span>
          ) : (
            can("spec.release") && (
              <Button
                size="sm"
                variant={status.kind === "released" ? "ghost" : "secondary"}
                icon={<Rocket className="size-3.5" />}
                onClick={() => setReleasing(true)}
              >
                Release…
              </Button>
            )
          )}
          {onExpand && <Button size="sm" variant="ghost" icon={<Maximize2 className="size-3.5" />} onClick={onExpand} title="Open the full editor" />}
        </span>
      </div>
      {wide && !inBom && <ItemBomStrip item={item} spec={spec} />}
      <Tabs
        level="record"
        className="px-2"
        tabs={tabs.map(([key, label]) => ({
          key,
          label,
          dot:
            TAB_FIELD[key] && local[TAB_FIELD[key] as TextField] !== undefined && local[TAB_FIELD[key] as TextField] !== spec[TAB_FIELD[key] as TextField]
              ? { tone: "warn" as const, title: "Not saved yet" }
              : undefined,
        }))}
        active={tab}
        onPick={(t) => {
          save();
          setTab(t);
        }}
      />
      <div className={cx("px-2 pt-2", wide ? "min-h-0 flex-1 overflow-y-auto pb-2" : "pb-1")}>
        {field && (
          <>
            {tab === "zh" && <div className="pb-1 text-[12px] text-muted">Optional Chinese text for Tailhoo and Elecrow, released with the English.</div>}
            {tab === "internal" && (
              <div className="pb-1 text-[12px] text-muted">HHL only: costs, other vendors, open questions. Never released and never shown to vendors.</div>
            )}
            {/* KiCad projects --- the board, as KiCad read it: a table from the data, above the text, never in it */}
            {tab !== "internal" && <BoardFacts spec={spec} lang={tab === "zh" ? "zh" : "en"} />}
            <MarkdownEditor
              key={`${spec.id}:${field}`}
              value={local[field] ?? spec[field]}
              onChange={change(field)}
              onSave={save}
              readOnly={!editable}
              wide={wide}
              lang={tab === "zh" ? "zh" : undefined}
              placeholder={tab === "zh" ? "中文…" : tab === "internal" ? "Notes for HHL only…" : "Write the spec…"}
              className={wide ? "h-full" : "h-[440px]"}
            />
          </>
        )}
        {tab === "files" && <SpecFiles spec={spec} ix={ix} wide={wide} />}
        {tab === "revisions" && <SpecRevisions spec={spec} ix={ix} wide={wide} />}
      </div>
      {releasing && (
        <ReleaseDialog
          spec={spec}
          ix={ix}
          title={title}
          text={dirty ? { draft_md: local.draft_md ?? spec.draft_md, draft_md_zh: local.draft_md_zh ?? spec.draft_md_zh } : undefined}
          beforeRelease={save}
          onClose={() => setReleasing(false)}
        />
      )}
    </div>
  );
}

/** Its name in the side pane's trail: "GCU2-ABXY spec". */
export function SpecTitle({ id }: { id: string }) {
  const item = useRow("item", id);
  const product = useSpecIndex().specByItem.get(id)?.shopify_product_id;
  return item ? `${(product && item.shopify_product_title) || item.sku || item.name} spec` : "Spec";
}

/**
 * The spec in the side pane (web/src/panes.tsx), the full editor: wherever it's opened from (the Specs page, a part's
 * Spec section, a PO's missing print file, a change notice), the page stays beside it. Closing it saves.
 */
export function SpecPane({ itemId }: { itemId: string }) {
  const item = useRow("item", itemId);
  const ix = useSpecIndex();
  const trail = usePane();
  const level = usePaneLevel();
  if (!item) return null;
  const spec = ix.specByItem.get(itemId);
  const path = itemPath(item);
  // Opened from the item's own panel: the trail already goes back to it
  const below = level ? trail[level - 1] : undefined;
  const fromItem = below?.kind === "item" && below.id === itemId;
  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* In the pane's title bar beside its name (PaneHead, ui/SidePane.tsx) */}
      <PaneHead fallback={(c) => <div className="min-w-0 px-4 pt-3 pb-2">{c}</div>}>
        <div className="min-w-0 truncate font-medium">
          {spec?.shopify_product_id || !item.shopify_variant_title
            ? item.shopify_product_title || item.name
            : `${item.shopify_product_title} · ${item.shopify_variant_title}`}
        </div>
        <div className="min-w-0 truncate text-muted">
          {/* A product's spec (migration 059) is every variant's: said, with the one it was opened from */}
          {(spec?.shopify_product_id ? [`Every variant (opened from ${item.sku})`, "Sell item"] : [item.sku, typeLabel(item)]).filter(Boolean).join(" · ")}
          {path && !fromItem && (
            <>
              {" · "}
              <PaneLink kind="item" id={item.id} to={path} className="text-accent-text hover:underline">
                Open item
              </PaneLink>
            </>
          )}
        </div>
      </PaneHead>
      <div className="flex min-h-0 flex-1 flex-col">{spec ? <SpecWorkspace item={item} spec={spec} ix={ix} wide /> : <NoSpec item={item} />}</div>
    </div>
  );
}
